/**
 * The one place that knows which brands exist.
 *
 * ADDING A BRAND: append its name to `BRAND_NAMES` below. TypeScript will then
 * fail every registry that is keyed by `BrandName` until you fill in the new
 * entry — see `src/config/brands/README.md` for the (short) checklist.
 *
 * This module is deliberately dependency-free: `vite.config.ts` imports it at
 * build time to resolve the pre-paint background, so it must not pull in MUI,
 * React, or anything that expects a browser.
 */

export const BRAND_NAMES = ["nova", "fitup", "stage"] as const;

export type BrandName = (typeof BRAND_NAMES)[number];

/** Used when `VITE_BRAND` is unset or unrecognised. */
export const DEFAULT_BRAND: BrandName = "fitup";

const isBrandName = (value: string | undefined): value is BrandName =>
  BRAND_NAMES.includes(value as BrandName);

/**
 * Resolve a raw `VITE_BRAND` value to a known brand.
 *
 * - unset / empty → `DEFAULT_BRAND`
 * - a known brand → that brand
 * - anything else → **throws**
 *
 * A set-but-unknown value is always a misconfiguration (a typo, or a brand
 * someone forgot to register). Falling back would ship a build wearing the
 * wrong brand with nothing but a warning to show for it — which is how `stage`
 * silently rendered Nova's theme. `vite.config.ts` calls this while building
 * its config, so the failure lands before `vite build` / `vite dev` does any
 * work.
 */
export const resolveBrandName = (raw: string | undefined): BrandName => {
  if (raw === undefined || raw === "") {
    return DEFAULT_BRAND;
  }
  if (isBrandName(raw)) {
    return raw;
  }
  throw new Error(
    `Unknown VITE_BRAND "${raw}". Known brands: ${BRAND_NAMES.join(", ")}. ` +
      `Leave VITE_BRAND unset to use the default ("${DEFAULT_BRAND}"), or ` +
      `register the brand — see src/config/brands/README.md. ` +
      `(Brand names are case-sensitive.)`,
  );
};
